import { useState, useEffect, useCallback, useRef } from 'react';
import { API_CONFIG } from '../constants/api';

// Dependendo de onde os tipos da sua aplicação moram, eles estariam aqui.
// Para manter simples e local, definirei uma interface mínima
export interface ChatMessage {
  id: string;
  senderId: string;
  appointmentId: string;
  content: string;
  createdAt: string;
}

export function useChat(appointmentId: string | undefined, token: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const eventSourceRef = useRef<EventSource | null>(null);

  // Carregar histórico
  const fetchHistory = useCallback(async () => {
    if (!appointmentId || !token) return;
    
    try {
      const apiUrl = API_CONFIG.BASE_URL;
      const response = await fetch(`${apiUrl}/chat/${appointmentId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao carregar histórico');
      
      const res = await response.json();
      setMessages(res.data || []);
    } catch (err: any) {
      setError(err);
      console.error(err);
    }
  }, [appointmentId, token]);

  // Instanciar SSE
  useEffect(() => {
    if (!appointmentId) return;

    // Carregar mensagens iniciais
    fetchHistory();

    const apiUrl = API_CONFIG.BASE_URL;
    // Passamos o token JWT via query parameter para autenticar o SSE
    const sseUrl = token 
      ? `${apiUrl}/chat/${appointmentId}/stream?token=${token}`
      : `${apiUrl}/chat/${appointmentId}/stream`;
    
    eventSourceRef.current = new EventSource(sseUrl);

    eventSourceRef.current.onopen = () => {
      console.log('SSE conectado para appointment:', appointmentId);
      setIsConnected(true);
      setError(null);
    };

    eventSourceRef.current.onmessage = (event) => {
      console.log('SSE mensagem recebida:', event.data);
      try {
        const newMessage: ChatMessage = JSON.parse(event.data);
        console.log('Mensagem parseada:', newMessage);
        setMessages((prev) => {
          // Evita duplicatas caso tenhamos enviado a mensagem via REST e também recebê-la por SSE
          if (!Array.isArray(prev)) return prev ?? [];
          if (prev.some(m => m.id === newMessage.id)) {
            console.log('Mensagem duplicada, ignorando:', newMessage.id);
            return prev;
          }
          console.log('Adicionando mensagem:', newMessage);
          return [...prev, newMessage];
        });
      } catch (e) {
        console.error('Error parsing SSE message', e);
      }
    };

    eventSourceRef.current.onerror = (err) => {
      console.warn('SSE Error / Desconectado. Tentando reconectar...', err);
      console.log('Estado do EventSource:', eventSourceRef.current?.readyState);
      setIsConnected(false);
      // Tentar reconectar após 5 segundos
      setTimeout(() => {
        if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
          console.log('Tentando reconectar SSE...');
          eventSourceRef.current = new EventSource(sseUrl);
          // Reattach handlers
          eventSourceRef.current.onopen = () => {
            console.log('SSE reconectado para appointment:', appointmentId);
            setIsConnected(true);
            setError(null);
          };
          eventSourceRef.current.onmessage = (event) => {
            console.log('SSE mensagem recebida após reconexão:', event.data);
            try {
              const newMessage: ChatMessage = JSON.parse(event.data);
              setMessages((prev) => {
                if (!Array.isArray(prev)) return prev ?? [];
                if (prev.some(m => m.id === newMessage.id)) return prev;
                return [...prev, newMessage];
              });
            } catch (e) {
              console.error('Error parsing SSE message after reconnect', e);
            }
          };
          eventSourceRef.current.onerror = (err) => {
            console.warn('SSE Error após reconexão:', err);
            setIsConnected(false);
          };
        }
      }, 5000);
    };

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [appointmentId, fetchHistory]);

  const sendMessage = async (content: string) => {
    if (!appointmentId || !token || !content.trim()) return null;

    console.log('Enviando mensagem:', content);
    try {
      const apiUrl = API_CONFIG.BASE_URL;
      const response = await fetch(`${apiUrl}/chat/${appointmentId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        throw new Error('Falha ao enviar mensagem');
      }

      const res = await response.json();
      console.log('Mensagem enviada com sucesso:', res);
      
      // Atualização otimista local
      const newMsg = res.data as ChatMessage;
      setMessages((prev) => {
        if (!Array.isArray(prev)) return prev ?? [];
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });

      return newMsg;

    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return {
    messages,
    isConnected,
    error,
    sendMessage,
    refetchHistory: fetchHistory
  };
}
